import React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import { CarouselNav } from "~components/shared/carousel";
import LazyRetinaImage from "~components/shared/lazy-retina-image/LazyRetinaImage";
import { LoopingVideo } from "~components/shared/video-player/LoopingVideo";
import type { CuratedItem, Video, Image } from "~types/types";

import styles from "../Media.module.css";
import {
    FIVE_SECONDS_IN_MILLISECONDS,
    CAROUSEL_ITEM_TYPES,
} from "../utils/constants";
import {
    type HydratedMediaCarouselData,
    type HydratedMediaCarouselItem,
    type MediaChildProps,
    type MediaConfig,
    MediaFormat,
} from "../utils/types";

/**
 * Exported validation method used by the Media Decider.
 * All Media subcomponents must expose this function.
 */
export function hasValidCarouselData(
    source: CuratedItem,
    format: MediaFormat
): boolean {
    return Boolean(source?.media?.carousel) && format === MediaFormat.carousel;
}

/**
 * INTERNAL COMPONENTS
 *
 * Use specific internal components for each carousel
 * item type. This keeps logic and markup distinct and allows
 * for specific styling. There should be one internal component
 * per property on CAROUSEL_ITEM_TYPES.
 */
const CarouselImageItem = ({
    hydratedCarouselItem,
    parent,
    config,
}: {
    hydratedCarouselItem: HydratedMediaCarouselItem;
    parent: CuratedItem;
    config: MediaConfig;
}) => {
    const { headline, url: parentUrl } = parent;
    const { credit, baseUrl } = hydratedCarouselItem as Image;
    const { aspectRatio, height, width, desktopMaxWidth, mobileMaxWidth } =
        config;

    if (!parentUrl || !baseUrl) return null;

    return (
        <div className={styles.carouselItemContainer}>
            <a
                href={parentUrl}
                className={createClasses([
                    styles.imageOverlayLink,
                    styles.linkedImage,
                ])}
                data-component="image-media-link"
            >
                <section
                    className={createClasses([
                        styles.image,
                        styles.defaultImage,
                        styles.carouselMediaItem,
                    ])}
                    data-component="media--carousel-iamge"
                >
                    <LazyRetinaImage
                        aspectRatio={aspectRatio}
                        image={hydratedCarouselItem}
                        altFallback={headline}
                        height={height}
                        width={width}
                        desktopMaxWidth={desktopMaxWidth}
                        mobileMaxWidth={mobileMaxWidth}
                        showCaption={Boolean(credit?.length)}
                    />
                </section>
            </a>
        </div>
    );
};

const CarouselLoopingVideoItem = ({
    hydratedCarouselItem,
    parentUrl,
    config,
}: {
    hydratedCarouselItem: HydratedMediaCarouselItem;
    parentUrl?: string;
    config: MediaConfig;
}) => {
    const { thumbnail, title } = hydratedCarouselItem as Video;
    const validTitle = title?.length ? title : undefined;

    const { desktopMaxWidth = 0, mobileMaxWidth = 0, width } = config;
    const imageWidth = width || Math.max(desktopMaxWidth, mobileMaxWidth);

    if (!hydratedCarouselItem?.url || !parentUrl) return null;

    return (
        <div className={styles.carouselItemContainer}>
            <a
                href={parentUrl}
                className={createClasses([
                    styles.imageOverlayLink,
                    styles.linkedImage,
                ])}
                data-component="image-media-link"
            >
                <section
                    className={createClasses([
                        styles.image,
                        styles.loopingVideo,
                        styles.carouselMediaItem,
                    ])}
                    data-component="media--carousel-looping-video"
                >
                    <LoopingVideo
                        credit={validTitle}
                        url={hydratedCarouselItem.url}
                        thumbnail={thumbnail}
                        showCaption={Boolean(validTitle)}
                        imageWidth={imageWidth}
                    />
                </section>
            </a>
        </div>
    );
};

export const CarouselMedia = ({ source, config = {} }: MediaChildProps) => {
    if (!source?.media?.carousel) return null;

    const { hydratedCarouselItems } = source.media
        .carousel as HydratedMediaCarouselData;

    const carouselItems = hydratedCarouselItems
        .map((hydratedCarouselItem, index) => {
            const { type } = hydratedCarouselItem;

            if (type === CAROUSEL_ITEM_TYPES.loopingVideo) {
                return (
                    <CarouselLoopingVideoItem
                        key={index + source.id}
                        hydratedCarouselItem={hydratedCarouselItem}
                        parentUrl={source.url}
                        config={config}
                    />
                );
            }

            return (
                <CarouselImageItem
                    key={index + source.id}
                    hydratedCarouselItem={hydratedCarouselItem}
                    parent={source}
                    config={config}
                />
            );
        })
        .filter(Boolean);

    if (!carouselItems?.length) return null;

    return (
        <div
            className={createClasses(
                config.className,
                styles.carouselContainer
            )}
        >
            <CarouselNav
                animatedTransition
                autoadvanceIntervalInMs={FIVE_SECONDS_IN_MILLISECONDS}
                noSpaceBetweenItems
            >
                {carouselItems}
            </CarouselNav>
        </div>
    );
};
